/*
  Sidebar menu: a native <dialog> that slides in from the left.
  showModal() gives the focus trap, Esc handling and an inert page behind it.
  Close with the X button, a tap outside the panel, or Esc.
*/
if (!customElements.get('sidebar-menu')) {
  class SidebarMenu extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-sidebar-open]');
      if (!this.dialog || !this.opener) return;

      this.opener.addEventListener('click', () => this.open());

      this.dialog.addEventListener('click', (event) => {
        // A tap on the dim area outside the panel lands on the dialog itself
        if (event.target === this.dialog || event.target.closest('[data-sidebar-close]')) this.close();
      });

      // Esc: run the slide-out instead of closing instantly
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      this.dialog.addEventListener('close', () => this.onClosed());

      // Back button restoring the page from the browser cache with the sidebar still open
      this.onPageShow = (event) => {
        if (event.persisted && this.dialog.open) this.dialog.close();
      };
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.onPageShow);
      document.body.classList.remove('overflow-hidden');
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    open() {
      if (this.dialog.open) return;
      document.body.classList.add('overflow-hidden');
      this.dialog.showModal();
      this.opener.setAttribute('aria-expanded', 'true');
      // Force layout so the slide-in transition starts from the closed position
      void this.dialog.offsetWidth;
      this.dialog.classList.add('is-open');
    }

    close() {
      if (!this.dialog.open || this.isClosing) return;
      this.isClosing = true;
      this.dialog.classList.remove('is-open');

      if (this.prefersReducedMotion) {
        this.finishClose();
        return;
      }

      const onEnd = (event) => {
        if (event.target === this.dialog && event.propertyName === 'transform') this.finishClose();
      };
      this.dialog.addEventListener('transitionend', onEnd);
      this.closeTimer = setTimeout(() => this.finishClose(), 450);
      this.removeEndListener = () => this.dialog.removeEventListener('transitionend', onEnd);
    }

    finishClose() {
      clearTimeout(this.closeTimer);
      if (this.removeEndListener) this.removeEndListener();
      this.removeEndListener = null;
      if (this.dialog.open) this.dialog.close();
    }

    onClosed() {
      this.isClosing = false;
      this.dialog.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      this.opener.setAttribute('aria-expanded', 'false');
      this.opener.focus();
    }
  }

  customElements.define('sidebar-menu', SidebarMenu);
}
